import React, { FC, useEffect, useState } from 'react';
import clsx from 'clsx';
import { DateView, DatePicker } from '@mui/x-date-pickers';
import dayjs, { Dayjs } from 'dayjs';

import './EventTimeSelector.scss';
import { useGeneralSettings } from '../../../../hooks/useGeneralSettings';
import { Switch, Typography } from '@mui/material';
import { useRecoilValue } from 'recoil';
import { selectedDateAtom, useEventDetailsMenuContext } from '../../../../data-access';
import { CustomTimeSelector } from '../../../common';
import { AccessTime, RepeatRounded } from '@mui/icons-material';
import { EventRecurrenceSelectDropdown } from '../EventRecurrenceSelectDropdown';
import { useTranslation } from 'react-i18next';
import { formatDataDate } from '../../../DataView/i18n';

interface EventTimeSelectorProps {
  startDateTime: Dayjs;
  endDateTime: Dayjs;
  onStartTimeChange: (date: Dayjs) => void;
  onEndTimeChange: (date: Dayjs) => void;
  className?: string;
  disabled?: boolean;
  isAllDay: boolean;
  setIsAllDay: (isAllDay: boolean) => void;
  showRecurrenceSelector?: boolean;
  recurrenceValue?: string | null;
  onRecurrenceChange?: (value: string | null) => void;
  recurrenceClassName?: string;
}

type OpenPicker = 'start' | 'end' | null;

const EventTimeSelector: FC<EventTimeSelectorProps> = ({
  startDateTime,
  endDateTime,
  onStartTimeChange,
  onEndTimeChange,
  className,
  disabled = false,
  isAllDay,
  setIsAllDay,
  showRecurrenceSelector = false,
  recurrenceValue = null,
  onRecurrenceChange,
  recurrenceClassName,
}) => {
  const { t, i18n } = useTranslation();
  const [openPicker, setOpenPicker] = useState<OpenPicker>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const {
    generalSettings: { timeFormat },
  } = useGeneralSettings();
  const { selectedEvent } = useEventDetailsMenuContext();
  const selectedDate = useRecoilValue(selectedDateAtom);

  // Collapse when the event changes
  useEffect(() => {
    setIsExpanded(false);
  }, [selectedEvent?.eventId]);

  const dayDiff = dayjs(endDateTime).diff(dayjs(startDateTime), 'day');
  const adjustedEndDate =
    isAllDay && selectedEvent?.start && selectedEvent?.end
      ? dayDiff > 0
        ? dayjs(startDateTime)
            .add(dayDiff - 1, 'day')
            .toDate()
        : endDateTime
      : endDateTime;

  const calendarSelectOptions: DateView[] = ['month', 'day'];

  const timeFormatStr = timeFormat === '12h' ? 'h:mm A' : 'HH:mm';
  const summaryDate = formatDataDate(i18n.language, startDateTime.toDate(), {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
  });
  const summaryTime = isAllDay
    ? t('calendar.common.allDay')
    : `${startDateTime.format(timeFormatStr)} – ${endDateTime.format(timeFormatStr)}`;

  const handleAllDayChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) return;
    setIsAllDay(e.target.checked);
    if (e.target.checked && selectedEvent?.start && selectedEvent?.end) {
      onStartTimeChange(dayjs(selectedEvent.start));
      onEndTimeChange(dayjs(selectedEvent.end));
    }
  };

  const handleStartDateChange = (date: Dayjs | null) => {
    if (disabled || !date) return;
    onStartTimeChange(date.hour(startDateTime.hour()).minute(startDateTime.minute()));
  };

  const handleEndDateChange = (date: Dayjs | null) => {
    if (disabled || !date) return;
    onEndTimeChange(date.hour(endDateTime.hour()).minute(endDateTime.minute()));
  };

  const handleStartTimeChange = (time: Dayjs | null) => {
    if (disabled || !time) return;
    onStartTimeChange(startDateTime.hour(time.hour()).minute(time.minute()));
  };

  const handleEndTimeChange = (time: Dayjs | null) => {
    if (disabled || !time) return;
    onEndTimeChange(endDateTime.hour(time.hour()).minute(time.minute()));
  };

  const renderDatePicker = (
    dateTime: Dayjs,
    onChange: (date: Dayjs | null) => void,
    pickerType: 'start' | 'end'
  ) => (
    <div className="event-time-selector__date">
      <DatePicker
        onClose={() => setOpenPicker(null)}
        open={openPicker === pickerType}
        value={dateTime}
        format="ddd, MMM DD"
        views={calendarSelectOptions}
        onChange={onChange}
        className="event-time-selector__date-picker"
        slotProps={{
          popper: {
            className: clsx('custom-time-picker-popper', 'custom-time-picker-popper--auto-height'),
          },
          textField: {
            variant: 'standard',
            disabled,
            InputProps: { disableUnderline: true },
            onClick: () => {
              if (!disabled) setOpenPicker(pickerType);
            },
          },
          openPickerIcon: { style: { display: 'none' } },
        }}
      />
    </div>
  );

  const renderTimeSelector = (dateTime: Dayjs, onChange: (time: Dayjs | null) => void) => (
    <div className="event-time-selector__time-selector">
      <CustomTimeSelector
        value={dateTime}
        onChange={onChange}
        amPm={timeFormat === '12h'}
        size="medium"
        disabled={disabled}
        showClearButton={false}
      />
    </div>
  );

  return (
    <div className={clsx('event-time-selector', className)}>
      {/* Collapsed summary row — click to expand */}
      <div
        className={clsx('event-time-selector__summary-row', {
          'event-time-selector__summary-row--expanded': isExpanded,
          'event-time-selector__summary-row--disabled': disabled,
        })}
        onClick={() => !disabled && setIsExpanded((v) => !v)}
      >
        <div className="event-time-selector__icon-container">
          <AccessTime className="event-time-selector__icon" />
        </div>
        <span className="event-time-selector__summary__date">{summaryDate}</span>
        <span className="event-time-selector__summary__time">{summaryTime}</span>
      </div>

      {/* Expanded pickers */}
      {isExpanded && (
        <div className="event-time-selector__expanded">
          {/* All day toggle */}
          <div className="event-time-selector__row">
            <div className="event-time-selector__icon-spacer" />
            <div className="event-time-selector__all-day">
              <Typography className="event-time-selector__all-day__label">
                {t('calendar.common.allDay')}
              </Typography>
              <Switch
                checked={isAllDay}
                onChange={handleAllDayChange}
                disabled={disabled}
                size="small"
              />
            </div>
          </div>

          {/* Start time row */}
          {!isAllDay && (
            <div className="event-time-selector__row event-time-selector__row--time">
              <div className="event-time-selector__icon-spacer" />
              {renderDatePicker(startDateTime, handleStartDateChange, 'start')}
              {renderTimeSelector(startDateTime, handleStartTimeChange)}
            </div>
          )}

          {/* End time row */}
          {!isAllDay && (
            <div className="event-time-selector__row event-time-selector__row--time">
              <div className="event-time-selector__icon-spacer" />
              {renderDatePicker(dayjs(adjustedEndDate), handleEndDateChange, 'end')}
              {renderTimeSelector(endDateTime, handleEndTimeChange)}
            </div>
          )}
        </div>
      )}

      {showRecurrenceSelector && onRecurrenceChange && (
        <>
          <hr className="event-time-selector__divider" />
          <div className="event-time-selector__row event-time-selector__row--recurrence">
            <div className="event-time-selector__icon-container">
              <RepeatRounded className="event-time-selector__icon" />
            </div>
            <EventRecurrenceSelectDropdown
              className={recurrenceClassName}
              value={recurrenceValue}
              onChange={onRecurrenceChange}
              startDate={startDateTime}
              disabled={disabled}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default EventTimeSelector;
